import { Button, Header, Input } from 'view/components';
import { FormContainer } from 'react-hook-form-mui';
import { SearchIcon, FilterIcon, CloseIcon } from 'assets';
import { FiltersBlock, CandidatesListBlock } from 'view/pages/candidates/components';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import { useGetCandidates } from 'view/pages/candidates/hooks/useGetCandidates';
import { yearsOfExperienceOptions } from 'view/pages/candidates/components/FiltersBlock';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as CS from 'view/modals/styled';
import { useContextSelector as useCS } from 'use-context-selector';
import { TUpdate } from 'domain/types';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { useNavigate } from 'react-router';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import { sortOptions } from 'view/pages/candidates/components/CandidatesListBlock';
import * as S from './styled';

const CandidateList = () => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('laptop'));
  const params = new URLSearchParams(window.location.search);
  const query: any = params.get('query');

  const [openDrawer, setOpenDrawer] = useState(false);
  const [page, setPage] = useState(1);
  const [queryAfterSubmit, setQueryAfterSubmit] = useState('');

  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const { data: positionTypeData, isLoading: isPositionTypeDataLoading } = useGetDropdownData(
    DropdownsEnum.POSITION_TYPE
  );

  const { data: educationData, isLoading: isEducationDataLoading } = useGetDropdownData(
    DropdownsEnum.EDUCATION
  );

  const { data: hardSkillsData, isLoading: isHardSkillsLoading } = useGetDropdownData(
    DropdownsEnum.SKILL,
    null,
    'Hard'
  );

  const { data: languageData, isLoading: isLanguageDataLoading } = useGetDropdownData(
    DropdownsEnum.LANGUAGE
  );

  const toggleDrawer = (newOpen: boolean) => {
    return () => {
      document.body.classList.toggle('no-scroll', newOpen);
      setOpenDrawer(newOpen);
    };
  };

  const { isLoading, data, getCandidates } = useGetCandidates();

  const hasFilters = (url) => {
    const params = new URLSearchParams(url);

    // Исключаемые ключи
    const excludedKeys = new Set(['query', 'sort', 'jobTitleId']);

    for (const [key, value] of params.entries()) {
      if (!excludedKeys.has(key) && value.trim() !== '') {
        return true; // Есть хотя бы один фильтр
      }
    }

    return false; // Нет фильтров
  };

  useEffect(() => {
    getCandidates(query || 'sort=relevance');
    if (globalUpdate) {
      globalUpdate({ isShowMatchScore: hasFilters(query) });
    }
    setQueryAfterSubmit(query || '');

    return () => {
      if (globalUpdate) globalUpdate({ isShowMatchScore: false });
    };
  }, []);

  const getQueryString = (filters) => {
    const params = new URLSearchParams();
    const jobLocationArray: Array<string> = [];
    // Простые текстовые поля
    if (filters.query) params.append('query', filters.query);

    // Объекты, где нужен id
    if (filters.sort) params.append('sort', filters.sort.value);
    /* if (filters.jobTitle) params.append('jobTitleId', filters.jobTitle.id); */
    if (filters.jobLocation) params.append('locationId', filters.jobLocation.id);
    /* if (filters.education) params.append('education', filters.education.id); */

    // Булевые флаги
    if (filters.onSite) jobLocationArray.push('onSite');
    if (filters.remote) jobLocationArray.push('remote');
    if (filters.hybrid) jobLocationArray.push('hybrid');

    // Диапазон зарплат
    if (filters.salaryRange?.from) params.append('salaryMin', filters.salaryRange.from);
    if (filters.salaryRange?.to) params.append('salaryMax', filters.salaryRange.to);
    if (filters.yearsOfExperience) {
      const selectedItem: any = yearsOfExperienceOptions.filter((item) => {
        return filters.yearsOfExperience === item.value;
      })[0];

      params.append('yearsOfExperienceMin', selectedItem.yearsOfExperienceMin);
      params.append('yearsOfExperienceMax', selectedItem.yearsOfExperienceMax);
    }

    // Обрабатываем массивы
    const appendLocationTypesArray = (key, array) => {
      if (Array.isArray(array) && array.length > 0) {
        params.append(
          key,
          array
            .map((item) => {
              return item;
            })
            .join(',')
        );
      }
    };

    const appendArray = (key, array) => {
      if (Array.isArray(array) && array.length > 0) {
        params.append(
          key,
          array
            .map((item) => {
              return item.id;
            })
            .join(',')
        );
      }
    };

    appendLocationTypesArray('locationTypes', jobLocationArray);
    appendArray('positionTypeIds', filters.positionType);
    appendArray('educationLevelIds', filters.education);
    appendArray(
      'skillIds',
      filters.hardSkills.map((item) => {
        return item.skill || item;
      })
    ); // Берем id из skill
    appendArray('languageIds', filters.languages);

    return params.toString();
  };

  const updateUrl = (queryString) => {
    const params = new URLSearchParams(window.location.search);
    params.set('query', queryString);
    navigate({
      pathname: window.location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const onSubmit = (page = 1) => {
    return (formData: any) => {
      const queryString = getQueryString(formData);
      setQueryAfterSubmit(queryString);

      if (globalUpdate) {
        globalUpdate({ isShowMatchScore: hasFilters(queryString) });
      }

      updateUrl(queryString);

      getCandidates(queryString, page);
      toggleDrawer(false)();
    };
  };

  const paramsFromQuery = useMemo(() => {
    return new URLSearchParams(`?${decodeURIComponent(query)}`);
  }, [query]);

  const getPositionTypesDefValue = useCallback(
    (positionTypeIds) => {
      if (!positionTypeIds || !positionTypeData) return null;
      const selectedIds = positionTypeIds?.split(',');
      const filteredItems = positionTypeData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [positionTypeData]
  );

  const getLocationTypesDefValue = useCallback((locationTypes) => {
    if (!locationTypes) return null;
    const selectedTypes = locationTypes?.split(',');
    return selectedTypes.reduce((acc, type) => {
      acc[type] = !!selectedTypes.includes(type);
      return acc;
    }, {});
  }, []);

  const getYearsOfExperienceDefValue = useCallback((yearsOfExperienceMin, yearsOfExperienceMax) => {
    if (!yearsOfExperienceMin || !yearsOfExperienceMax) return '';
    const filteredItems = yearsOfExperienceOptions.filter((item) => {
      return (
        item.yearsOfExperienceMin === +yearsOfExperienceMin &&
        item.yearsOfExperienceMax === +yearsOfExperienceMax
      );
    });
    return filteredItems?.[0]?.value || '';
  }, []);

  const getEducationDefValue = useCallback(
    (educationLevelIds) => {
      if (!educationLevelIds || !educationData) return null;
      const selectedIds = educationLevelIds?.split(',');
      const filteredItems = educationData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [educationData]
  );

  const getTechSkillsDefValue = useCallback(
    (skillIds) => {
      if (!skillIds || !hardSkillsData) return null;
      const selectedIds = skillIds?.split(',');
      const filteredItems = hardSkillsData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [hardSkillsData]
  );

  const getLanguagesDefValue = useCallback(
    (languageIds) => {
      if (!languageIds || !languageData) return null;
      const selectedIds = languageIds?.split(',');
      const filteredItems = languageData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [languageData]
  );

  const getSortOptionDefValue = useCallback((sortValue) => {
    if (!sortValue || !sortOptions)
      return {
        id: 1,
        name: 'Relevance score',
        value: 'relevance',
      };
    const filteredItems = sortOptions?.filter((item) => {
      return item.value === sortValue;
    });
    return (
      filteredItems?.[0] || {
        id: 1,
        name: 'Relevance score',
        value: 'relevance',
      }
    );
  }, []);

  const defaultValues = {
    query: paramsFromQuery?.get('query') || '',
    sort: getSortOptionDefValue(paramsFromQuery?.get('sort')),
    positionType: getPositionTypesDefValue(paramsFromQuery?.get('positionTypeIds')) || null,
    jobLocation: null,
    onSite: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.onSite || false,
    remote: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.remote || false,
    hybrid: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.hybrid || false,
    yearsOfExperience:
      getYearsOfExperienceDefValue(
        paramsFromQuery?.get('yearsOfExperienceMin'),
        paramsFromQuery?.get('yearsOfExperienceMax')
      ) || '',
    education: getEducationDefValue(paramsFromQuery?.get('educationLevelIds')) || null,
    salaryRange: {
      from: paramsFromQuery?.get('salaryMin') || '',
      to: paramsFromQuery?.get('salaryMax') || '',
    },
    hardSkills: getTechSkillsDefValue(paramsFromQuery?.get('skillIds')) || [],
    languages: getLanguagesDefValue(paramsFromQuery?.get('languageIds')) || [],
  };

  return (
    <S.Wrapper style={{ overflowY: openDrawer ? 'hidden' : 'auto' }}>
      <Header />
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit(1)}
      >
        <S.SearchContainer>
          <Input
            name="query"
            label=""
            placeholder="Search by job titles, skills, qualifications..."
            marginbottom={0.1}
            startAdornment={<SearchIcon style={{ marginRight: '8px' }} />}
          />
          <Button
            variant="contained"
            type="submit"
            onClick={() => {
              return setPage(1);
            }}
            text={matches ? '' : 'Search'}
            endIcon={matches ? <SearchIcon /> : null}
            style={
              matches
                ? {
                    padding: '10px',
                    width: '40px',
                    minWidth: '40px',
                    height: '40px',
                    minHeight: '40px',
                  }
                : undefined
            }
          />
          {matches && (
            <Button
              variant="outlined"
              text=""
              endIcon={<FilterIcon />}
              onClick={toggleDrawer(true)}
              style={{
                padding: '10px',
                width: '40px',
                minWidth: '40px',
                height: '40px',
                minHeight: '40px',
              }}
            />
          )}
        </S.SearchContainer>
        <S.MainContentWrapper>
          {matches ? (
            <div
              style={{
                display: openDrawer ? 'initial' : 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: 'white',
                width: '100vw',
                height: '100vh',
                zIndex: 1000,
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  padding: '24px 24px 0 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <p
                  style={{
                    color: '#2B163F',
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '120%',
                  }}
                >
                  Filters
                </p>
                <CS.CloseIconBox onClick={toggleDrawer(false)}>
                  <CloseIcon />
                </CS.CloseIconBox>
              </div>
              <FiltersBlock
                onSubmit={onSubmit}
                setPage={setPage}
                positionTypeData={positionTypeData}
                isPositionTypeDataLoading={isPositionTypeDataLoading}
                educationData={educationData}
                isEducationDataLoading={isEducationDataLoading}
                hardSkillsData={hardSkillsData}
                isHardSkillsLoading={isHardSkillsLoading}
                languageData={languageData}
                isLanguageDataLoading={isLanguageDataLoading}
              />
            </div>
          ) : (
            <FiltersBlock
              onSubmit={onSubmit}
              setPage={setPage}
              positionTypeData={positionTypeData}
              isPositionTypeDataLoading={isPositionTypeDataLoading}
              educationData={educationData}
              isEducationDataLoading={isEducationDataLoading}
              hardSkillsData={hardSkillsData}
              isHardSkillsLoading={isHardSkillsLoading}
              languageData={languageData}
              isLanguageDataLoading={isLanguageDataLoading}
            />
          )}
          <CandidatesListBlock
            isLoading={isLoading}
            data={data}
            onSubmit={onSubmit}
            page={page}
            setPage={setPage}
            queryAfterSubmit={queryAfterSubmit}
          />
        </S.MainContentWrapper>
      </FormContainer>
    </S.Wrapper>
  );
};

export default CandidateList;
